import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { X } from 'lucide-react';
import { FlyerPage } from '../../types';
import { DroppableSlot } from './DroppableSlot';

interface FlyerPageViewProps {
  page: FlyerPage;
  pageIndex: number;
  onRemoveProduct?: (slotIndex: number) => void;
  onRemoveFooter?: () => void;
  isEditable?: boolean;
  validFrom?: string | null;
  validTo?: string | null;
}

export const FlyerPageView: React.FC<FlyerPageViewProps> = ({
  page,
  pageIndex,
  onRemoveProduct,
  onRemoveFooter,
  isEditable = true,
  validFrom,
  validTo,
}) => {
  const isFirstPage = page.pageNumber === 1;

  const { setNodeRef: setFooterRef, isOver: isFooterOver } = useDroppable({
    id: `page-${pageIndex}-footer`,
    disabled: !isEditable || !isFirstPage,
  });

  // Calculate grid placement for promo images
  const getGridPlacement = (index: number, slot: any) => {
    const col = (index % 2) + 1;
    const row = Math.floor(index / 2) + 1;

    if (slot?.type === 'promo' && slot.promoSize) {
      switch (slot.promoSize) {
        case 'horizontal':
        case 'header_2x1':
          return { gridColumn: `${col} / span 2`, gridRow: `${row} / span 1` };
        case 'square':
        case 'header_2x2':
          return { gridColumn: `${col} / span 2`, gridRow: `${row} / span 2` };
        case 'full_page':
          return { gridColumn: '1 / span 2', gridRow: '1 / span 4' };
        default:
          return { gridColumn: col, gridRow: row };
      }
    }

    return { gridColumn: col, gridRow: row };
  };

  // Check if slot is occupied by multi-slot promo
  const isOccupiedByPromo = (index: number) => {
    const col = index % 2;
    const row = Math.floor(index / 2);

    for (let i = 0; i < page.slots.length; i++) {
      const slot = page.slots[i];
      if (slot?.type === 'promo' && slot.promoSize && i !== index) {
        const promoCol = i % 2;
        const promoRow = Math.floor(i / 2);

        switch (slot.promoSize) {
          case 'horizontal':
          case 'header_2x1':
            if (row === promoRow && col > promoCol && col <= promoCol + 1) return true;
            break;
          case 'square':
          case 'header_2x2':
            if (row >= promoRow && row < promoRow + 2 && col >= promoCol && col < promoCol + 2) return true;
            break;
          case 'full_page':
            return true;
        }
      }
    }
    return false;
  };

  const getPromoImageUrl = (id: string) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    return `${API_URL}/promo-images/${id}/image`;
  };

  // KLÍČOVÉ: A4 rozměry a layout
  const containerWidth = 700; // A4 šířka
  const containerHeight = 1020;
  const padding = 32; // 2× p-4
  const gapSize = 8; // gap-2 = 8px
  const borderSize = 4; // border-2 = 2px z každé strany

  // Výška patičky se DOPOČÍTÁVÁ z doporučeného poměru promo typu "Patička" (2380×228),
  // aby se do pruhu vešla přesně a nezůstaly po stranách bílé okraje. Dřív tu bylo
  // natvrdo 60 px, což dávalo poměr 11.79:1 proti 10.44:1 obrázku -> contain nechával
  // ~37 px bílého místa na každé straně. PDF má patičku 595×57, tedy přesně 10.44:1.
  const FOOTER_RATIO = 2380 / 228;
  const footerInnerWidth = containerWidth - borderSize - padding - borderSize;
  const footerHeight = Math.round(footerInnerWidth / FOOTER_RATIO) + borderSize;
  // Vnitrni vyska stranky: box-sizing border-box, takze od containerHeight odecist padding
  // I ramecek. Driv se ramecek (4 px) nepocital, obsah byl o 4 px vyssi nez misto a flexbox
  // je ubral patičce -> obrazek se zmensil a po stranach zustaly bile pruhy, datum ujelo.
  const contentHeight = containerHeight - padding - borderSize;

  // Na stránce 1: první řada menší (footer zabírá místo), ostatní 3 řady normální
  // Na ostatních stránkách: všechny 4 řady stejně vysoké
  let firstRowHeight: number;
  let normalRowHeight: number;

  if (isFirstPage) {
    // Normální výška řádku (stejná jako na stránce 2)
    normalRowHeight = Math.floor((contentHeight - 3 * gapSize) / 4);
    // První řada = zbývající místo po odečtení footeru a 3 normálních řad
    firstRowHeight = contentHeight - footerHeight - gapSize - (3 * normalRowHeight) - (3 * gapSize);
  } else {
    // Všechny řady stejně vysoké
    normalRowHeight = Math.floor((contentHeight - 3 * gapSize) / 4);
    firstRowHeight = normalRowHeight;
  }

  // Definitivní výška gridu (součet tracků + mezer). Bez ní má grid výšku "auto"
  // a procentní výšky uvnitř buněk (h-full) se nemusí spolehlivě vyřešit -> promo
  // obrázek se roztáhne na přirozenou výšku a přeteče do patičky.
  const gridHeight = firstRowHeight + normalRowHeight * 3 + gapSize * 3;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="border-2 border-gray-300 rounded-lg p-4 bg-white" style={{ width: `${containerWidth}px`, height: `${containerHeight}px` }}>
        <div className="flex flex-col gap-2 h-full">
          {/* Grid s FIXNÍ výškou řádků - první řada může být menší na stránce 1 */}
          <div
            className="grid grid-cols-2 gap-2"
            style={{
              gridTemplateRows: `${firstRowHeight}px ${normalRowHeight}px ${normalRowHeight}px ${normalRowHeight}px`,
              height: `${gridHeight}px`,
            }}
          >
          {page.slots.map((slot, index) => {
            if (isOccupiedByPromo(index)) return null;

            const gridStyle = getGridPlacement(index, slot);

            // Přesouvat/vyměňovat v letáku lze jen obsazené jednoslotové položky
            // (produkt nebo promo velikosti 1×1). Víceslotová promo (hlavička, 2×1, 2×2…) ne.
            const isSingleOccupied =
              !!slot &&
              slot.type !== 'empty' &&
              (slot.type === 'product' ||
                (slot.type === 'promo' && (!slot.promoSize || slot.promoSize === 'single')));
            const dragId =
              isEditable && isSingleOccupied ? `move-page-${pageIndex}-slot-${index}` : undefined;

            return (
              <div key={`${pageIndex}-${index}`} style={gridStyle} className="h-full">
                <DroppableSlot
                  id={`page-${pageIndex}-slot-${index}`}
                  slot={slot || { type: 'empty' }}
                  onRemove={isEditable && onRemoveProduct ? () => onRemoveProduct(index) : undefined}
                  index={index}
                  dragId={dragId}
                />
              </div>
            );
          })}
          </div>

          {/* Patička na stránce 1 */}
          {isFirstPage && (
            <div
              ref={setFooterRef}
              className={`border-2 rounded-lg overflow-hidden transition-colors relative flex-shrink-0 ${
                isFooterOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
              }`}
              style={{ height: `${footerHeight}px` }}
            >
              {page.footerPromoImage ? (
                <div className="relative h-full">
                  {/* Patička = contain, stejně jako všechny ostatní obrázky a jako PDF
                      (renderPromoSlot kreslí přes fit). Dřív tu bylo cover, aby se u správně
                      nastřižené patičky schovaly boční okraje - náhled má poměr 11.13:1, PDF
                      10.44:1. Jenže u obrázku se špatným poměrem cover extrémně přiblížil
                      (u otočené patičky 228×2380 byla vidět necelá procenta plochy), takže
                      náhled ukazoval něco jiného než PDF. Cena za contain: ~3 px okraje po
                      stranách u správné patičky. */}
                  <img
                    src={getPromoImageUrl(page.footerPromoImage.id)}
                    alt={page.footerPromoImage.name}
                    className="w-full h-full object-contain"
                  />
                  {/* Render date if fillDate is true */}
                  {page.footerPromoImage.fillDate && validTo && (
                    // Na stred cerveneho pole "Platnost letaku" (91,65 % sirky) - stejne jako PDF.
                    // Velikost v pomeru k patičce jako v PDF (10 pt na 595 pt sirky); pevnych 14 px
                    // (text-sm) bylo v 660px nahledu o tretinu vetsi a datum vytekalo z pole.
                    <div
                      className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 text-white font-bold whitespace-nowrap leading-none"
                      style={{
                        left: '91.65%',
                        fontSize: `${(footerInnerWidth * 10) / 595.28}px`,
                        fontFamily: 'Vodafone Rg, Arial, sans-serif',
                      }}
                    >
                      {new Date(validTo).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\s/g, '')}
                    </div>
                  )}
                  {/* Krizek vlevo - vpravo zakryval pole s datem platnosti. */}
                  {isEditable && onRemoveFooter && (
                    <button
                      onClick={onRemoveFooter}
                      className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  Patička (2cm) - Přetáhněte sem promo obrázek
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
